// Eigenbau: MR-Liste mit Detail (Beschreibung, Commits, Änderungen/Diffs);
// Tremor hat keine Merge-Request-Ansicht und die Detaildaten kommen live aus
// der GitLab-API (GITLAB_HOST/GITLAB_TOKEN), nicht aus einem Baustein.
import { Fragment, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import type {
  QualitaetsGateEntscheidung,
  QualitaetsGateModus,
} from "@cubic/shared";
import { api } from "@/lib/api";
import { schluessel } from "@/lib/abfragen";
import { Badge } from "@/components/Badge";
import { Button } from "@/components/Button";
import { cx } from "@/lib/utils";
import { Mikrolabel } from "@/components/entwurf/Mikrolabel";
import { Zustandsmarke } from "@/components/entwurf/Zustandspunkt";
import { zeileKlassen } from "@/components/entwurf/Zeile";
import { STAND } from "@/lib/entwurf-zustaende";
import {
  qualitaetsGateAnzeige,
  type QualitaetsGateKurz,
} from "@/lib/qualitaets-gate-zustand";
import { DiffAnsicht } from "./DiffAnsicht";
import { QualitaetsGateAbschnitt } from "./QualitaetsGateAbschnitt";
import { ChevronRight } from "lucide-react";

export type MergeRequestAnzeige = {
  id: string;
  gitlabIid: number;
  titel: string;
  zustand: string;
  quellBranch: string;
  zielBranch: string;
  webUrl: string;
  beschreibung: string | null;
  kopfSha: string | null;
  entwurf: boolean | null;
  qualitaetsGate: QualitaetsGateKurz & {
    modus: QualitaetsGateModus;
    entscheidung: QualitaetsGateEntscheidung;
    gruende: Array<{
      code: string;
      art: "ausstehend" | "blockiert" | "nicht_bewertbar";
      meldung: string;
      anzahl?: number;
      bezugId?: string;
    }>;
    pruefungId: string | null;
    kopfSha: string | null;
  };
  /** ISO-Zeitpunkte (tRPC serialisiert Datumswerte als Strings). */
  gitlabErstelltAm: string | null;
  gitlabGeaendertAm: string | null;
  angelegtAm: string;
  geaendertAm: string;
};

export type MrCommitAnzeige = {
  id: string;
  kurzId: string | null;
  titel: string | null;
  nachricht: string | null;
  autor: string | null;
  wann: string | null;
  webUrl: string | null;
};

export type MrAenderungAnzeige = {
  alterPfad: string;
  neuerPfad: string;
  istNeu: boolean;
  istUmbenannt: boolean;
  istGeloescht: boolean;
  altModus: string | null;
  neuModus: string | null;
  diff: string | null;
  statistik: { hinzugefuegt: number; entfernt: number };
};

export type MrZustandAnzeige = {
  label: string;
  /** Die Marke im Detail. */
  badge: "default" | "success" | "neutral" | "warning" | "error";
  /** Die Zustandsspalte in der Liste. */
  klasse: string;
};

/**
 * Wie ein Merge-Request-Zustand aussieht — offen hebt sich ab, der Rest ruht.
 *
 * **Warum `success` hier nicht mehr vorkommt.** `styles.css` bindet `blue-*`
 * und `emerald-*` auf **dieselbe** Hausskala um. Die Marke „Offen" (Variante
 * `default`, blau) und „Gemergt" (`success`, emerald) trugen damit Stufe für
 * Stufe identische Werte, und in der Liste stand für beide wörtlich
 * `text-tinte`. Zwei Zustände, ein Bild: Auf der Lieferungsseite war Offenes
 * von Erledigtem nicht zu unterscheiden.
 *
 * Getrennt wird entlang der Entwurfsregel — der Akzent gehört dem, was eine
 * Handlung verlangt. Offen trägt ihn, Gemergtes tritt in ruhige Tinte zurück,
 * Geschlossenes eine Stufe blasser. Gesperrt behält die Warnfarbe: Das ist
 * kein Zustand, sondern ein Aufhalter.
 */
export function zustandAnzeige(zustand: string): MrZustandAnzeige {
  switch (zustand.trim().toLowerCase()) {
    case "opened":
      return {
        label: "Offen",
        badge: "default",
        klasse: "font-medium text-akzent",
      };
    case "merged":
      return { label: "Gemergt", badge: "neutral", klasse: "text-tinte3" };
    case "closed":
      return { label: "Geschlossen", badge: "neutral", klasse: "text-tinte4" };
    case "locked":
      return {
        label: "Gesperrt",
        badge: "warning",
        klasse: "text-[var(--zustand-wartet)]",
      };
    default:
      return {
        label: zustand || "Unbekannt",
        badge: "neutral",
        klasse: "text-tinte4",
      };
  }
}

function formatiereZeitpunkt(wert: string | null): string {
  if (!wert) return "—";
  return new Date(wert).toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function MrGitlabZeitangaben({
  mr,
}: {
  mr: Pick<
    MergeRequestAnzeige,
    "gitlabErstelltAm" | "gitlabGeaendertAm"
  >;
}) {
  return (
    <>
      <span>
        In GitLab erstellt: {formatiereZeitpunkt(mr.gitlabErstelltAm)}
      </span>
      <span>
        In GitLab aktualisiert: {formatiereZeitpunkt(mr.gitlabGeaendertAm)}
      </span>
    </>
  );
}

function sichereHttpUrl(wert: string): string | null {
  try {
    const url = new URL(wert);
    return url.protocol === "https:" || url.protocol === "http:"
      ? url.toString()
      : null;
  } catch {
    return null;
  }
}

/** Ein Query-Bezug für das Muster — derselbe Schlüssel wie in den Einstellungen. */
function reviewAppMusterQuery(projektId: string) {
  return {
    queryKey: [
      "cubic",
      "einstellung",
      "review_app_muster",
      "projekt",
      projektId,
    ],
    queryFn: () =>
      api.einstellungen.lies.query({
        schluessel: "review_app_muster",
        bereich: "projekt",
        bereichId: projektId,
      }),
  };
}

/** Adresse der Review-Umgebung eines Merge Requests — oder null. */
function reviewAppUrl(
  muster: string | undefined,
  iid: number,
): string | null {
  if (!muster) return null;
  return sichereHttpUrl(muster.replaceAll("{iid}", String(iid)));
}

function istOffen(zustand: string): boolean {
  return zustand.trim().toLowerCase() === "opened";
}

/**
 * Review-Apps der offenen Merge Requests — eine Zeile je Umgebung.
 *
 * Die Adresse ist eine Konvention, kein GitLab-Datum: Das Muster kommt aus
 * der Projekteinstellung `review_app_muster`, die Nummer aus dem Merge
 * Request. Ob die Umgebung gerade läuft, weiß nur sie selbst — der Verweis
 * führt hin, behauptet aber keinen Zustand.
 */
function ReviewAppAbschnitt({
  projektId,
  mrs,
}: {
  projektId: string;
  mrs: MergeRequestAnzeige[];
}) {
  const muster = useQuery(reviewAppMusterQuery(projektId));
  const eintraege = mrs
    .filter((mr) => istOffen(mr.zustand))
    .map((mr) => ({ mr, url: reviewAppUrl(muster.data, mr.gitlabIid) }))
    .filter(
      (e): e is { mr: MergeRequestAnzeige; url: string } => e.url !== null,
    );
  if (eintraege.length === 0) return null;
  return (
    <div className="mb-6" data-testid="review-apps">
      <Mikrolabel grad={11.5}>Review-Apps</Mikrolabel>
      <div className="mt-1">
        {eintraege.map(({ mr, url }) => (
          <div
            key={mr.id}
            className="flex flex-wrap items-center gap-3.5 border-b border-linie px-2 py-2"
          >
            <span className="flex-none font-mono text-[11.5px] text-tinte4">
              !{mr.gitlabIid}
            </span>
            <span
              title={mr.titel}
              className="min-w-0 max-w-[420px] flex-1 truncate text-[13.5px] text-tinte"
            >
              {mr.titel}
            </span>
            <a
              className="flex-none font-mono text-[11.5px] text-blue-600 underline dark:text-blue-400"
              href={url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {new URL(url).host}
            </a>
          </div>
        ))}
      </div>
    </div>
  );
}

function GateKurzmarke({ gate }: { gate: QualitaetsGateKurz }) {
  const anzeige = qualitaetsGateAnzeige(gate);
  const bild = STAND[anzeige.stand];
  return (
    <Zustandsmarke
      bild={bild}
      label={anzeige.label}
      ton={bild.text}
      strich={bild.strich}
      className="flex-none"
    />
  );
}

function Teilladung({
  isLoading,
  isError,
  erneut,
  children,
}: {
  isLoading: boolean;
  isError: boolean;
  erneut: () => void;
  children: ReactNode;
}) {
  if (isLoading) {
    return <p className="text-sm text-gray-500">Lade …</p>;
  }
  if (isError) {
    return (
      <div className="space-y-2" role="alert">
        <p className="text-sm text-red-600">
          Die Daten konnten nicht geladen werden.
        </p>
        <Button variant="secondary" onClick={erneut}>
          Erneut versuchen
        </Button>
      </div>
    );
  }
  return <div className="space-y-2">{children}</div>;
}

/**
 * Klartext nach einer manuellen Q&A-Auslösung.
 *
 * Die Auslösung selbst ist immer „erfolgreich", sobald der Server den Anlass
 * angenommen hat — ob daraus Arbeit entsteht, entscheiden die Automationen.
 * Genau das muss die Meldung sagen, sonst klickt man ins Leere und hält es
 * für einen Lauf.
 */
export function qaAusloesungMeldung(
  ergebnis: {
    angelegt: ReadonlyArray<unknown>;
    verlaengert: ReadonlyArray<unknown>;
  } | null,
): { text: string; mitVerweis: boolean } {
  if (ergebnis && ergebnis.angelegt.length > 0) {
    const zahl = ergebnis.angelegt.length;
    return {
      text: `Q&A-Lauf ausgelöst — ${zahl} ${zahl === 1 ? "Auftrag" : "Aufträge"} angelegt.`,
      mitVerweis: true,
    };
  }
  if (ergebnis && ergebnis.verlaengert.length > 0) {
    return {
      text: "Ausgelöst — ein offener Auftrag derselben Automation wurde verlängert.",
      mitVerweis: true,
    };
  }
  return {
    text: "Ausgelöst, aber keine aktive Automation hört auf „Manuell ausgelöst“ — es entstand kein Auftrag.",
    mitVerweis: false,
  };
}

/**
 * Aufgeklapptes Detail eines Merge Requests — direkt unter seiner Zeile.
 * Commits und Änderungen kommen live aus der GitLab-API; die Komponente
 * wird nur gerendert, solange der MR ausgewählt ist.
 */
function MrDetail({
  projektId,
  mr,
}: {
  projektId: string;
  mr: MergeRequestAnzeige;
}) {
  const queryClient = useQueryClient();
  /*
   * Der Knopf steht bei jedem Zustand, auch bei „Gemergt": Der Server prüft
   * keinen MR-Zustand, und GitLab liefert den Diff eines MRs auch nach dem
   * Merge noch aus. Gerade der nachträgliche Lauf auf einem Gemergten ist ein
   * gewollter Fall — etwa wenn die Automation erst danach eingerichtet wurde.
   */
  const qaAusloesen = useMutation({
    mutationFn: () =>
      api.autoQa.manuellAusloesen.mutate({
        projektId,
        zielArt: "merge_request",
        zielId: mr.id,
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["cubic", "auto-qa"] }),
  });
  const einrichtung = useQuery({
    queryKey: schluessel.gitlabEinrichtung(projektId),
    queryFn: () => api.gitlab.einrichtung.query({ projektId }),
  });
  const commits = useQuery({
    queryKey: schluessel.gitlabMrCommits(projektId, mr.id),
    queryFn: () => api.gitlab.mrCommits.query({ projektId, mrId: mr.id }),
    enabled: einrichtung.data?.eingerichtet === true,
  });
  const aenderungen = useQuery({
    queryKey: schluessel.gitlabMrAenderungen(projektId, mr.id),
    queryFn: () =>
      api.gitlab.mrAenderungen.query({ projektId, mrId: mr.id }),
    enabled: einrichtung.data?.eingerichtet === true,
  });
  const [offeneDiffDatei, setzeOffeneDiffDatei] = useState<string | null>(
    null,
  );
  const muster = useQuery(reviewAppMusterQuery(projektId));
  const reviewUrl = istOffen(mr.zustand)
    ? reviewAppUrl(muster.data, mr.gitlabIid)
    : null;

  const apiVerfuegbar = einrichtung.data?.eingerichtet ?? false;
  const einrichtungGeladen = einrichtung.data !== undefined;

  return (
    <div className="space-y-4" data-testid="mr-detail">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-50">
          !{mr.gitlabIid} · {mr.titel}
        </h4>
        <span className="flex flex-wrap items-center gap-3">
          {reviewUrl ? (
            <a
              className="text-sm font-medium text-blue-600 underline dark:text-blue-400"
              href={reviewUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Review-App öffnen
            </a>
          ) : null}
          {sichereHttpUrl(mr.webUrl) ? (
            <a
              className="text-sm font-medium text-blue-600 underline dark:text-blue-400"
              href={sichereHttpUrl(mr.webUrl)!}
              target="_blank"
              rel="noopener noreferrer"
            >
              In GitLab öffnen
            </a>
          ) : null}
          <Button
            variant="secondary"
            onClick={() => qaAusloesen.mutate()}
            disabled={qaAusloesen.isPending}
            isLoading={qaAusloesen.isPending}
            loadingText="Löse aus …"
          >
            Q&A-Lauf auslösen
          </Button>
        </span>
      </div>

      {qaAusloesen.isError ? (
        <p className="text-sm text-red-600" role="alert">
          Der Q&A-Lauf konnte nicht ausgelöst werden:{" "}
          {qaAusloesen.error instanceof Error
            ? qaAusloesen.error.message
            : "Unbekannter Fehler."}
        </p>
      ) : null}
      {qaAusloesen.isSuccess
        ? (() => {
            const meldung = qaAusloesungMeldung(qaAusloesen.data.ergebnis);
            return (
              <p className="text-sm text-gray-700 dark:text-gray-300">
                {meldung.text}
                {meldung.mitVerweis ? (
                  <>
                    {" "}
                    <Link
                      to="/auto-qa"
                      search={{}}
                      className="text-akzent underline decoration-1 underline-offset-[3px]"
                    >
                      Zu den Q&A-Aufträgen
                    </Link>
                  </>
                ) : null}
              </p>
            );
          })()
        : null}

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
        <span className="flex items-center gap-1.5">
          <Badge variant={zustandAnzeige(mr.zustand).badge}>
            {zustandAnzeige(mr.zustand).label}
          </Badge>
        </span>
        <MrGitlabZeitangaben mr={mr} />
        <span className="font-mono">
          {mr.quellBranch || "?"} → {mr.zielBranch || "?"}
        </span>
        {mr.entwurf ? <span>Entwurf</span> : null}
        {mr.kopfSha ? (
          <span className="font-mono">Kopf {mr.kopfSha.slice(0, 8)}</span>
        ) : null}
      </div>

      <QualitaetsGateAbschnitt projektId={projektId} mrId={mr.id} />

      {mr.beschreibung ? (
        <p className="whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300">
          {mr.beschreibung}
        </p>
      ) : null}

      {einrichtungGeladen && !apiVerfuegbar ? (
        <p className="rounded-lg bg-amber-100 px-3 py-2 text-sm text-amber-900 dark:bg-amber-950/50 dark:text-amber-200">
          Commits und Änderungen werden live aus der GitLab-API geladen. Dafür
          fehlt auf dem Server die Einrichtung von GITLAB_HOST und
          GITLAB_TOKEN.
        </p>
      ) : null}

      <div>
        <h5 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          Commits
        </h5>
        <div className="mt-1">
          <Teilladung
            isLoading={commits.isLoading}
            isError={commits.isError}
            erneut={() => void commits.refetch()}
          >
            {commits.data?.eingerichtet === true &&
            commits.data.commits.length === 0 ? (
              <p className="text-sm text-gray-500">Keine Commits.</p>
            ) : null}
            {commits.data?.eingerichtet === true
              ? commits.data.commits.map((commit) => (
                  <div
                    key={commit.id}
                    className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-sm"
                  >
                    <span className="font-mono text-xs text-gray-400">
                      {commit.kurzId ?? commit.id.slice(0, 8)}
                    </span>
                    <span className="text-gray-900 dark:text-gray-100">
                      {commit.titel ?? commit.nachricht ?? commit.id}
                    </span>
                    <span className="text-xs text-gray-500">
                      {commit.autor ?? ""}
                      {commit.autor && commit.wann ? " · " : ""}
                      {commit.wann ? formatiereZeitpunkt(commit.wann) : ""}
                    </span>
                    {commit.webUrl ? (
                      <a
                        className="text-xs text-blue-600 underline dark:text-blue-400"
                        href={commit.webUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        öffnen
                      </a>
                    ) : null}
                  </div>
                ))
              : null}
          </Teilladung>
        </div>
      </div>

      <div>
        <h5 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          Änderungen
        </h5>
        <div className="mt-1">
          <Teilladung
            isLoading={aenderungen.isLoading}
            isError={aenderungen.isError}
            erneut={() => void aenderungen.refetch()}
          >
            {aenderungen.data?.eingerichtet === true &&
            aenderungen.data.aenderungen.length === 0 ? (
              <p className="text-sm text-gray-500">Keine Änderungen.</p>
            ) : null}
            {aenderungen.data?.eingerichtet === true
              ? aenderungen.data.aenderungen.map((aenderung) => {
                  const schluessel = `${aenderung.alterPfad}→${aenderung.neuerPfad}`;
                  const offen = offeneDiffDatei === schluessel;
                  return (
                    <div
                      key={schluessel}
                      className="overflow-hidden rounded-lg bg-white dark:bg-gray-925"
                    >
                      <button
                        type="button"
                        className="flex w-full flex-wrap items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-900"
                        onClick={() =>
                          setzeOffeneDiffDatei(offen ? null : schluessel)
                        }
                        disabled={!aenderung.diff}
                      >
                        <span className="flex flex-wrap items-center gap-2">
                          <span className="font-mono text-xs text-gray-500">
                            {aenderung.alterPfad}
                            {aenderung.alterPfad !== aenderung.neuerPfad
                              ? ` → ${aenderung.neuerPfad}`
                              : ""}
                          </span>
                          {aenderung.istNeu ? (
                            <Badge variant="success">neu</Badge>
                          ) : null}
                          {aenderung.istUmbenannt ? (
                            <Badge variant="neutral">umbenannt</Badge>
                          ) : null}
                          {aenderung.istGeloescht ? (
                            <Badge variant="error">gelöscht</Badge>
                          ) : null}
                        </span>
                        <span className="flex items-center gap-2 font-mono text-xs">
                          <span className="text-emerald-600 dark:text-emerald-400">
                            +{aenderung.statistik.hinzugefuegt}
                          </span>
                          <span className="text-rose-600 dark:text-rose-400">
                            −{aenderung.statistik.entfernt}
                          </span>
                          {aenderung.diff ? (
                            <span className="text-gray-400">
                              {offen ? "Diff ausblenden" : "Diff anzeigen"}
                            </span>
                          ) : null}
                        </span>
                      </button>
                      {offen && aenderung.diff ? (
                        <div className="px-3 pb-3 pt-1">
                          <DiffAnsicht diff={aenderung.diff} />
                        </div>
                      ) : null}
                    </div>
                  );
                })
              : null}
          </Teilladung>
        </div>
      </div>
    </div>
  );
}

export function MrAbschnitt({
  projektId,
  mrs,
  isLoading,
  isError,
  erneut,
}: {
  projektId: string;
  mrs: MergeRequestAnzeige[];
  isLoading: boolean;
  isError: boolean;
  erneut: () => void;
}) {
  const [ausgewaehlteId, setzeAusgewaehlteId] = useState<string | null>(null);

  if (isLoading) {
    return (
      <p className="rounded-lg bg-gray-50 px-4 py-6 text-center text-sm text-gray-400 dark:bg-gray-900 dark:text-gray-500">
        Lade Merge Requests …
      </p>
    );
  }
  if (isError) {
    return (
      <div
        className="space-y-2 rounded-lg bg-red-50 px-4 py-3 dark:bg-red-950/40"
        role="alert"
      >
        <p className="text-sm text-red-700 dark:text-red-400">
          Merge Requests konnten nicht geladen werden.
        </p>
        <Button variant="secondary" onClick={erneut}>
          Erneut versuchen
        </Button>
      </div>
    );
  }
  if (mrs.length === 0) {
    return (
      <p className="rounded-lg bg-gray-50 px-4 py-6 text-center text-sm text-gray-500 dark:bg-gray-900 dark:text-gray-400">
        Noch keine Merge Requests gespeichert. Sie erscheinen hier, sobald
        GitLab den ersten übergibt.
      </p>
    );
  }

  return (
    <div>
      <ReviewAppAbschnitt projektId={projektId} mrs={mrs} />
      <div data-testid="mr-liste">
        {mrs.map((mr) => {
          const anzeige = zustandAnzeige(mr.zustand);
          const ausgewaehlt = ausgewaehlteId === mr.id;
          const detailId = `mr-detail-${mr.id}`;
          return (
            <Fragment key={mr.id}>
              <button
                type="button"
                className={cx(
                  zeileKlassen({
                    hoehe: "auto",
                    klickbar: true,
                    className: "flex-wrap gap-3.5 px-2",
                  }),
                  ausgewaehlt && "bg-wisch",
                )}
                onClick={() => setzeAusgewaehlteId(ausgewaehlt ? null : mr.id)}
                aria-expanded={ausgewaehlt}
                aria-controls={detailId}
              >
                <span
                  className={cx(
                    "flex-none font-mono text-[11.5px] text-tinte4",
                    "inline-flex items-center gap-1.5",
                  )}
                >
                  <span
                    className={cx(
                      "inline-flex text-tinte3 transition-transform duration-150 ease-aus",
                      ausgewaehlt && "rotate-90",
                    )}
                  >
                    <ChevronRight className="size-3.5" aria-hidden="true" />
                  </span>
                  !{mr.gitlabIid}
                </span>
                <span
                  className={cx(
                    "flex-none font-mono text-[11.5px]",
                    anzeige.klasse,
                  )}
                >
                  {anzeige.label}
                </span>
                <span
                  title={mr.titel}
                  className="min-w-0 max-w-[420px] flex-1 truncate text-[13.5px] text-tinte"
                >
                  {mr.titel}
                </span>
                <GateKurzmarke gate={mr.qualitaetsGate} />
                <span className="hidden min-w-0 max-w-72 flex-none truncate font-mono text-[10.5px] text-tinte4 lg:inline">
                  {mr.quellBranch || "?"} → {mr.zielBranch || "?"}
                </span>
                <Mikrolabel grad={11} className="ml-auto hidden flex-none sm:inline">
                  {formatiereZeitpunkt(mr.gitlabGeaendertAm)}
                </Mikrolabel>
              </button>
              {ausgewaehlt ? (
                <div
                  id={detailId}
                  role="region"
                  aria-label={`Merge Request !${mr.gitlabIid}: ${mr.titel}`}
                  className="border-b border-linie bg-wisch/60 px-2 py-5"
                >
                  <MrDetail projektId={projektId} mr={mr} />
                </div>
              ) : null}
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}
